/** My Tasks — unified workflow inbox across treasury approvals, EPBCS workflow & AI proposals. */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowsClockwise, CheckCircle, Tray, ShieldCheck, Calculator, Sparkle,
         ArrowRight, CircleNotch } from "@phosphor-icons/react";

const SOURCE_ICONS = { treasury: ShieldCheck, epbcs: Calculator, ai: Sparkle };

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function MyTasksPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/my-tasks").then((r) => setData(r.data)).catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const groups = {};
  (data?.tasks || []).forEach((t) => {
    (groups[t.source_label] = groups[t.source_label] || { source: t.source, items: [] }).items.push(t);
  });

  return (
    <div className="flex flex-col min-h-full bg-[#F8F9FA]">
      <div className="flex-none bg-white border-b border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C8A24B]">Workflow Inbox</div>
            <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-[#0F172A]" data-testid="my-tasks-title">
              My Tasks
              {data && data.count > 0 && (
                <span className="ml-3 align-middle inline-flex items-center justify-center min-w-[26px] h-[26px] px-1.5 rounded-full bg-[#0B1E3F] text-white text-[13px] font-bold" data-testid="my-tasks-count">
                  {data.count}
                </span>
              )}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <p className="hidden xl:block max-w-md text-right text-xs leading-relaxed text-slate-500">
              Everything waiting on you — treasury approvals, budget workflow actions and AI proposals — in one place.
            </p>
            <button onClick={load} disabled={loading} data-testid="my-tasks-refresh"
              className="flex items-center gap-1.5 border border-slate-300 text-slate-600 px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded-sm hover:bg-slate-50 disabled:opacity-50">
              {loading ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} weight="bold" />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 md:p-6 w-full max-w-5xl">
        {!data && <div className="text-sm text-slate-500" data-testid="my-tasks-loading">Loading…</div>}

        {data && data.count === 0 && (
          <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-10 text-center" data-testid="my-tasks-empty">
            <CheckCircle size={36} weight="fill" className="text-[#1F6B4A] mx-auto" />
            <p className="mt-3 text-[15px] font-semibold text-[#0F172A]">You're all caught up</p>
            <p className="mt-1 text-xs text-slate-500">No pending approvals, workflow actions or AI proposals need your attention.</p>
          </div>
        )}

        <div className="space-y-4">
          {Object.entries(groups).map(([label, g]) => {
            const Icon = SOURCE_ICONS[g.source] || Tray;
            return (
              <div key={label} className="rounded-sm border border-slate-200 bg-white shadow-sm" data-testid={`tasks-group-${g.source}`}>
                <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
                  <Icon size={15} weight="fill" className="text-[#C8A24B]" />
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.12em] text-[#0B1E3F]">{label}</h3>
                  <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full bg-slate-100 text-slate-600 text-[11px] font-bold">
                    {g.items.length}
                  </span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {g.items.map((t, i) => (
                    <li key={i} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50/70 transition-colors"
                      data-testid={`task-item-${g.source}-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13.5px] font-semibold text-[#0F172A] truncate">{t.title}</div>
                        {t.detail && <div className="mt-0.5 text-[11.5px] text-slate-500 truncate">{t.detail}</div>}
                      </div>
                      <span className="flex-none text-[10px] text-slate-400 tabular-nums">{timeAgo(t.at)}</span>
                      <button onClick={() => nav(t.link)} data-testid={`task-open-${g.source}-${i}`}
                        className="flex-none inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm hover:bg-[#132a52]">
                        {t.action_label} <ArrowRight size={11} weight="bold" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
